import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { BackButton, EditButton } from "../../../../Components/Utils/Buttons";
import Breadcrumb from "../../../../Components/Utils/Breadcrumb";
import { NormalHeading } from "../../../../Components/Utils/Heading";
import { CopyButton } from "../../../../Components/Others/PremiumTable";
import ModalContainer from "../../../../Components/Others/ModalContainer";
import { useDeleteData } from "../../../../Components/Hooks/useDeleteData";
import { GetAccess, GetRole } from "../../../../Components/Utils/roles";
import axiosInstance from "../../../../Services/axiosCommon";

const ViewBank = () => {
  const { bankViewId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const stateData = location.state?.data;
  const pageIndex = location.state?.pageIndex;
  const searchQuery = location.state?.searchQuery;

  const [data, setData] = useState(stateData || null);
  const [loading, setLoading] = useState(!stateData);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const canEdit = GetAccess("banks_edit") || GetRole();
  const canDelete = GetAccess("banks_delete") || GetRole();

  const fetchBankDetails = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/banks/${bankViewId}`);
      if (res.data?.data) {
        setData(res.data.data);
      } else if (res.data) {
        setData(res.data);
      }
    } catch (err) {
      console.error("Error fetching bank details:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (bankViewId) {
      fetchBankDetails();
    }
  }, [bankViewId]);

  const { handle: handleDeleteData } = useDeleteData(() => {
    navigate("/payments/banks", {
      state: { currentPage: pageIndex, searchQuery },
    });
  });

  const handleConfirmDelete = () => {
    handleDeleteData(`/banks/${bankViewId}`, setDeleteConfirmOpen);
  };

  if (loading) {
    return (
      <div className="w-full h-full py-8 px-4 flex flex-col items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#048178] border-t-transparent rounded-full animate-spin mb-3"></div>
        <p className="text-sm font-medium text-gray-500">
          Loading Bank Information...
        </p>
      </div>
    );
  }

  return (
    <div className="w-full h-full p-2 lg:px-4">
      {/* Top Header */}
      <div className="w-full flex flex-row justify-between items-center gap-2 mb-3">
        <Breadcrumb
          items={[
            { name: "Payment", link: "/payments/banks" },
            { name: "Bank Details", link: "/payments/banks" },
            { name: data?.bankName || "View Bank" },
          ]}
        />
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            to="/payments/banks"
            state={{ currentPage: pageIndex, searchQuery }}
          >
            <BackButton link={true} />
          </Link>

          {canEdit && (
            <Link
              to={`/payments/banks/edit/${bankViewId}`}
              state={{
                data,
                from: `/payments/banks/view/${bankViewId}`,
                pageIndex,
                searchQuery,
              }}
            >
              <EditButton height="8" />
            </Link>
          )}
{/* 
          {canDelete && (
            <button
              onClick={() => setDeleteConfirmOpen(true)}
              className="px-3 h-8 text-xs 2xl:text-sm font-semibold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-lg inline-flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
              title="Delete Bank"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              <span>Delete</span>
            </button>
          )} */}
        </div>
      </div>

      {/* Main Content Box */}
      <div className="w-full mx-auto md:h-[calc(100vh-145px)] bg-white rounded-md mt-4 overflow-y-auto p-2 lg:p-3">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
          <NormalHeading title={data?.bankName || "Bank Account Details"} />
          {/* <div className="flex flex-wrap items-center gap-2">
            <div className="border border-[#048178] overflow-hidden rounded-md text-xs 2xl:text-sm flex items-center">
              <p className="px-2 py-[5px] text-[#048178]">Currency :</p>
              <p className="bg-[#048178] text-white px-2 py-[5px]">
                {data?.currencyName || data?.currency || "SAR"}
              </p>
            </div>
          </div> */}
        </div>

        {/* 2-Column Info Grid matching Customers & Suppliers */}
        <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Account Information */}
          <div className="w-full rounded-lg bg-white py-2 flex flex-col">
            <div className="odd:bg-white even:bg-slate-50 w-full flex px-2 items-center">
              <p className="w-1/3 py-2 text-xs font-medium text-[#111537] shrink-0">
                Bank Name
              </p>
              <p className="w-2/3 py-2 text-xs text-[#606279] break-words">
                : {data?.bankName || "-"}
              </p>
            </div>

            <div className="odd:bg-white even:bg-slate-50 w-full flex px-2 items-center">
              <p className="w-1/3 py-2 text-xs font-medium text-[#111537] shrink-0">
                Account Name
              </p>
              <p className="w-2/3 py-2 text-xs text-[#606279] break-words">
                : {data?.accountName || "-"}
              </p>
            </div>

            <div className="odd:bg-white even:bg-slate-50 w-full flex px-2 items-center">
              <p className="w-1/3 py-2 text-xs font-medium text-[#111537] shrink-0">
                Account Number
              </p>
              <div className="w-2/3 py-2 text-xs text-[#606279] flex items-center">
                <span>: {data?.accountNumber || "-"}</span>
                {data?.accountNumber && (
                  <CopyButton text={data?.accountNumber} />
                )}
              </div>
            </div>

            <div className="odd:bg-white even:bg-slate-50 w-full flex px-2 items-center">
              <p className="w-1/3 py-2 text-xs font-medium text-[#111537] shrink-0">
                IBAN
              </p>
              <div className="w-2/3 py-2 text-xs font-mono text-[#606279] flex items-center">
                <span>: {data?.iban || "-"}</span>
                {data?.iban && <CopyButton text={data?.iban} />}
              </div>
            </div>

            <div className="odd:bg-white even:bg-slate-50 w-full flex px-2 items-center">
              <p className="w-1/3 py-2 text-xs font-medium text-[#111537] shrink-0">
                SWIFT / BIC Code
              </p>
              <div className="w-2/3 py-2 text-xs text-[#606279] flex items-center">
                <span>: {data?.swiftCode || "-"}</span>
                {data?.swiftCode && <CopyButton text={data?.swiftCode} />}
              </div>
            </div>

            <div className="odd:bg-white even:bg-slate-50 w-full flex px-2 items-center">
              <p className="w-1/3 py-2 text-xs font-medium text-[#111537] shrink-0">
                Currency
              </p>
              <p className="w-2/3 py-2 text-xs font-semibold text-[#048178] break-words">
                : {data?.currencyName || data?.currency || "SAR"}
              </p>
            </div>
          </div>

          {/* Branch & Address Details */}
          <div className="w-full rounded-lg bg-white py-2 flex flex-col">
            <div className="odd:bg-white even:bg-slate-50 w-full flex px-2 items-center">
              <p className="w-1/3 py-2 text-xs font-medium text-[#111537] shrink-0">
                Address Line 1
              </p>
              <p className="w-2/3 py-2 text-xs text-[#606279] break-words">
                : {data?.address?.address1 || data?.address1 || "-"}
              </p>
            </div>

            <div className="odd:bg-white even:bg-slate-50 w-full flex px-2 items-center">
              <p className="w-1/3 py-2 text-xs font-medium text-[#111537] shrink-0">
                Address Line 2
              </p>
              <p className="w-2/3 py-2 text-xs text-[#606279] break-words">
                : {data?.address?.address2 || data?.address2 || "-"}
              </p>
            </div>

            <div className="odd:bg-white even:bg-slate-50 w-full flex px-2 items-center">
              <p className="w-1/3 py-2 text-xs font-medium text-[#111537] shrink-0">
                City
              </p>
              <p className="w-2/3 py-2 text-xs text-[#606279] break-words">
                : {data?.address?.city || data?.city || "-"}
              </p>
            </div>

            <div className="odd:bg-white even:bg-slate-50 w-full flex px-2 items-center">
              <p className="w-1/3 py-2 text-xs font-medium text-[#111537] shrink-0">
                District
              </p>
              <p className="w-2/3 py-2 text-xs text-[#606279] break-words">
                : {data?.address?.district || data?.district || "-"}
              </p>
            </div>

            <div className="odd:bg-white even:bg-slate-50 w-full flex px-2 items-center">
              <p className="w-1/3 py-2 text-xs font-medium text-[#111537] shrink-0">
                State / Province
              </p>
              <p className="w-2/3 py-2 text-xs text-[#606279] break-words">
                : {data?.address?.state || data?.state || "-"}
              </p>
            </div>

            <div className="odd:bg-white even:bg-slate-50 w-full flex px-2 items-center">
              <p className="w-1/3 py-2 text-xs font-medium text-[#111537] shrink-0">
                Country
              </p>
              <p className="w-2/3 py-2 text-xs text-[#606279] break-words">
                : {data?.address?.country || data?.country || "-"}
              </p>
            </div>

            <div className="odd:bg-white even:bg-slate-50 w-full flex px-2 items-center">
              <p className="w-1/3 py-2 text-xs font-medium text-[#111537] shrink-0">
                Postal Code
              </p>
              <p className="w-2/3 py-2 text-xs text-[#606279] break-words">
                : {data?.address?.postalCode || data?.postalCode || "-"}
              </p>
            </div>
          </div>
        </div>
      </div>

      <ModalContainer
        visiable={deleteConfirmOpen}
        title="Delete Bank Account"
        content={`Are you sure you want to delete "${data?.bankName} - ${data?.accountNumber}"? This action cannot be undone.`}
        handleClick={handleConfirmDelete}
        closeModal={() => setDeleteConfirmOpen(false)}
      />
    </div>
  );
};

export default ViewBank;
